import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/solid';
import '../index.css';
import { getDisplayName } from '../utils/displayName';

interface HeroProps {
  onFormSubmit?: (formData: {first_name: string, last_name: string, email: string}) => Promise<void>;
  loading?: boolean;
  success?: boolean;
  error?: string;
  onRedirect?: () => void;
}

const Hero: React.FC<HeroProps> = ({
  onFormSubmit,
  loading = false,
  success = false,
  error = '',
  onRedirect
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
  });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (onFormSubmit) {
      await onFormSubmit(formData);
    }
  };

  const handleRedirect = () => {
    if (onRedirect) {
      onRedirect();
    } else if (isAuthenticated && user) {
  if (user.is_admin) navigate('/admin/dashboard'); else if (user.role === 'professor') navigate('/professor-pathway'); else navigate('/student-pathway');
    } else {
      navigate('/login');
    }
  };

  return (
    <div id="hero" className="min-h-full flex flex-col justify-start pl-4 lg:pl-12 pr-4 py-8 lg:py-16 pb-24 lg:pb-16 gap-12">
      {/* Text Content Section - Far Left Aligned */}
      <div className="text-left max-w-xl lg:max-w-2xl">
        <p className="py-2 text-xl lg:text-2xl text-white font-medium text-shadow-lg">MUSLIM STUDY ABROAD</p>
        <h1 className="font-custom-thin text-white leading-tight py-4 text-4xl lg:text-6xl font-bold text-shadow-xl">
          Join A Muslim Study Abroad Adventure Today!
        </h1>
        <p className="font-custom text-white py-4 text-base lg:text-lg leading-relaxed text-shadow-md">
          A place where students of all backgrounds are invited to embark on an enriching journey of cross-cultural exploration and academic advancement
        </p>
      </div>

      {/* Form Section - Below Text, Also Far Left */}
      <div className="w-full max-w-sm lg:max-w-md">
        <div className="bg-black/20 backdrop-blur-lg rounded-3xl p-6 lg:p-8 border border-white/20 shadow-2xl">
          {success ? (
            /* Success Message */
            <div className="text-center">
              <div className="mx-auto mb-6 w-18 h-18 bg-green-50/90 rounded-full flex items-center justify-center border-2 border-green-500">
                <CheckIcon className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-white drop-shadow-lg mb-3">
                Thank You!
              </h3>
              <p className="text-white drop-shadow-md mb-6 text-base leading-relaxed">
                Your profile is created! We’re taking you to Step 1 so you can secure your account, verify contact info, and finish the Dreaming questionnaire.
              </p>
              <div 
                onClick={handleRedirect}
                className="cursor-pointer bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/30 hover:bg-white/20 transition-all duration-200 hover:border-white/50"
              >
                <p className="text-sm text-white drop-shadow-md italic text-center">
                  Click here if you’re not redirected automatically.
                </p>
              </div>
            </div>
          ) : isAuthenticated && user ? (
            /* Already Logged In Message */
            <div className="text-center">
              <div className="mx-auto mb-6 w-18 h-18 bg-blue-50/90 rounded-full flex items-center justify-center border-2 border-blue-500">
                <CheckCircleIcon className="w-9 h-9 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-white drop-shadow-lg mb-3">
                Welcome Back, {getDisplayName(user)}!
              </h3>
              <p className="text-white drop-shadow-md mb-6 text-base leading-relaxed">
                You're already logged in. Ready to continue your study abroad journey?
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => { if (user.is_admin) navigate('/admin/dashboard'); else if (user.role === 'professor') navigate('/professor-pathway'); else navigate('/student-pathway'); }}
                  className="w-full bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold py-3 px-6 rounded-xl hover:from-emerald-700 hover:to-green-700 focus:outline-none focus:ring-4 focus:ring-emerald-200 transform transition duration-200 hover:scale-105 shadow-lg"
                >
                  {user.is_admin ? 'Go to Dashboard' : 'View My Pathway'}
                </button>
                <p className="text-white/80 text-sm">
                  {user.is_admin ? 'Manage prospects and applications' : 'Track your pathway progress'}
                </p>
              </div>
            </div>
          ) : (
            /* Registration Form */
            <>
              {error && (
                <div className="mb-6 p-4 bg-red-50/90 border border-red-200 rounded-xl border-l-4 border-l-red-600">
                  <p className="text-red-600 text-sm m-0 font-medium drop-shadow-sm">{error}</p>
                </div>
              )}

              <div className="mb-4">
                <p className="text-emerald-200 text-xs uppercase tracking-[0.3em]">Access Program Details</p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-semibold text-white drop-shadow-lg mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base outline-none transition-all duration-200 bg-gray-50 text-slate-900 placeholder:text-slate-400 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-emerald-400 dark:focus:bg-slate-900/90 dark:focus:ring-emerald-700/40"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-semibold text-white drop-shadow-lg mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base outline-none transition-all duration-200 bg-gray-50 text-slate-900 placeholder:text-slate-400 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-emerald-400 dark:focus:bg-slate-900/90 dark:focus:ring-emerald-700/40"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-white drop-shadow-lg mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base outline-none transition-all duration-200 bg-gray-50 text-slate-900 placeholder:text-slate-400 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-emerald-400 dark:focus:bg-slate-900/90 dark:focus:ring-emerald-700/40"
                    placeholder="you@example.com"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full text-white font-bold py-4 px-6 rounded-xl border-2 text-base cursor-pointer flex items-center justify-center transition-all duration-300 drop-shadow-lg hover:-translate-y-0.5 hover:scale-[1.01] hover:bg-white/10 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:scale-100 disabled:hover:bg-transparent ${
                    loading 
                      ? 'bg-gray-400/50 border-gray-400/50' 
                      : 'bg-transparent border-emerald-200/80 hero-nudge'
                  }`}
                >
                  {loading ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 mr-3 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Unlock Program Details
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Hero
